/**
 * iter140 — shared display card for Fleet Vehicles + Equipment attached
 * to a Quote or Invoice.
 *
 * Fetches the tenant fleet/equipment lists in parallel and filters to the IDs
 * actually attached to the record. Renders nothing when nothing is attached.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Label } from "./ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FleetEquipmentAttached({ fleetIds = [], equipmentIds = [] }) {
  const [fleet, setFleet] = useState([]);
  const [equipment, setEquipment] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const [fRes, eRes] = await Promise.all([
        fleetIds.length ? axios.get(`${API}/fleet/vehicles`).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        equipmentIds.length ? axios.get(`${API}/equipment`).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      ]);
      if (cancelled) return;
      setFleet((fRes.data || []).filter((v) => fleetIds.includes(v.id)));
      setEquipment((eRes.data || []).filter((e) => equipmentIds.includes(e.id)));
    };
    load();
    return () => { cancelled = true; };
  }, [fleetIds.join(","), equipmentIds.join(",")]);

  if (fleet.length === 0 && equipment.length === 0) return null;

  return (
    <Card data-testid="fleet-equipment-attached-card">
      <CardHeader>
        <CardTitle>Fleet & Equipment</CardTitle>
        <CardDescription>
          {fleet.length} vehicle(s), {equipment.length} equipment item(s)
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {fleet.length > 0 && (
          <div>
            <Label className="text-muted-foreground">Vehicles</Label>
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              {fleet.map((v) => (
                <div
                  key={v.id}
                  className="rounded-lg border bg-muted/30 p-3"
                  data-testid={`fleet-attached-row-${v.id}`}
                >
                  <p className="font-medium text-sm">
                    {v.name || [v.year, v.make, v.model].filter(Boolean).join(" ") || "Vehicle"}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {v.license_plate || v.vin || v.type || "\u2014"}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
        {equipment.length > 0 && (
          <div>
            <Label className="text-muted-foreground">Equipment</Label>
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              {equipment.map((e) => (
                <div
                  key={e.id}
                  className="rounded-lg border bg-muted/30 p-3"
                  data-testid={`equipment-attached-row-${e.id}`}
                >
                  <p className="font-medium text-sm">{e.name || "Equipment"}</p>
                  <p className="text-xs text-muted-foreground">
                    {e.serial_number || e.category || "\u2014"}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
